import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Cal, { getCalApi } from "@calcom/embed-react";
import { Sparkles, Clock, TrendingDown, Cpu, UserCheck } from 'lucide-react';
import useSEO from '../hooks/useSEO';
import { ROUTE_DEFAULTS, SITE_URL } from '../config/seo';
import FAQ from '../components/FAQ';
import LeadCaptureForm from '../components/LeadCaptureForm';
import { contactFaqs } from '../data/faqs';

const processSteps = [
  {
    number: "01",
    title: "Discovery call",
    description:
      "A focused 30-minute working session. We map your goals, audience, channels, and current creative bottlenecks, then send a written action plan the same day. No slide decks, no sales pitch — you leave with clear next steps even if we do not end up working together.",
    rgb: "139, 92, 246",
  },
  {
    number: "02",
    title: "Scope & quote",
    description:
      "Within two business days we send a fixed-fee scope covering deliverables, timeline, channels, and the AI pipeline we will run. No hourly billing, no surprise production fees. You review, mark changes, and we align on cost and schedule before a single asset ships.",
    rgb: "59, 130, 246",
  },
  {
    number: "03",
    title: "Creative kickoff",
    description:
      "Signed scope triggers kickoff within a week. We build your prompt architecture, brand tuning, and reference boards, then introduce your senior creative director and the AI production team. First drafts land in your inbox inside 48 hours of kickoff — not weeks later.",
    rgb: "236, 72, 153",
  },
  {
    number: "04",
    title: "Iteration & delivery",
    description:
      "Drafts ship fast so we iterate on what actually resonates. You review in Figma or a shared drive, leave comments, and we rerun the pipeline the same day. Final assets arrive with full commercial usage rights, production-ready formats, and per-channel variants tuned for Meta, TikTok, YouTube, and LinkedIn.",
    rgb: "34, 211, 238",
  },
];

const trustSignals = [
  {
    Icon: Clock,
    title: "48h first-draft delivery",
    description:
      "From signed scope to your first creative review in two days, not two weeks.",
    rgb: "139, 92, 246",
  },
  {
    Icon: TrendingDown,
    title: "90% production cost reduction",
    description:
      "AI-native pipelines replace shoot days, rentals, and revision rounds with prompt iteration.",
    rgb: "59, 130, 246",
  },
  {
    Icon: Cpu,
    title: "AI-native creative pipeline",
    description:
      "Midjourney, Veo, Runway, Kling, and custom prompt systems tuned to your brand voice.",
    rgb: "236, 72, 153",
  },
  {
    Icon: UserCheck,
    title: "Senior creative direction",
    description:
      "A human creative director reviews every asset before delivery — no unsupervised AI output.",
    rgb: "34, 211, 238",
  },
];

const Contact = () => {
  useSEO({
    ...ROUTE_DEFAULTS['/contact'],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      '@id': `${SITE_URL}/contact#webpage`,
      url: `${SITE_URL}/contact`,
      name: 'Contact Cultiv AI Studio',
      description:
        'Book a free 30-minute strategy call with Cultiv AI Studio to plan your next AI-powered viral marketing campaign.',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: { '@id': `${SITE_URL}/#organization` },
      mainEntity: {
        '@type': 'ContactPoint',
        contactType: 'sales',
        areaServed: 'Worldwide',
        availableLanguage: ['English'],
        url: `${SITE_URL}/contact`,
        email: 'hello@cultiv-ai.studio',
      },
    },
  });

  useEffect(() => {
    (async function () {
      const cal = await getCalApi({ "namespace": "30min" });
      cal("ui", { "hideEventTypeDetails": false, "layout": "month_view" });
    })();
  }, []);

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-20">
      <div className="max-w-[1000px] mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-20 text-center"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 mb-8 text-xs tracking-widest uppercase text-violet-300 border border-violet-500/30 rounded-full backdrop-blur-sm"
            style={{
              boxShadow: '0 0 20px rgba(139, 92, 246, 0.2), inset 0 0 20px rgba(139, 92, 246, 0.1)',
            }}
          >
            <Sparkles size={14} className="text-violet-400" />
            Free strategy call
          </motion.span>
          <h1 className="text-6xl md:text-8xl font-light tracking-tight mb-6">
            Start Your Viral Campaign Planning Now!
          </h1>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            Stop wasting budget on slow production. Let&apos;s build your AI marketing engine today.
          </p>
        </motion.div>

        {/* Calendly Embed */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-20 w-full h-[600px] md:h-[700px]"
        >
          <Cal
            namespace="30min"
            calLink="cultiv-ai-studio/30min"
            style={{ width: "100%", height: "100%", overflow: "scroll" }}
            config={{ "layout": "month_view" }}
          />
        </motion.div>

        {/* How we work */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8 }}
          className="mb-24"
          aria-labelledby="contact-process-heading"
        >
          <div className="text-center mb-14">
            <h2
              id="contact-process-heading"
              className="text-4xl md:text-5xl font-light tracking-tight mb-4"
            >
              How we work
            </h2>
            <p className="text-base md:text-lg text-white/60 max-w-2xl mx-auto">
              Four stages from first call to final delivery. No mystery, no padding.
            </p>
          </div>

          <ol className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {processSteps.map((step, idx) => (
              <motion.li
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6, delay: idx * 0.08 }}
                className="relative p-8 border rounded-2xl bg-gradient-to-br from-white/[0.03] to-transparent neon-border neon-border-hover"
                style={{
                  borderColor: `rgba(${step.rgb}, 0.2)`,
                  boxShadow: `0 0 20px rgba(${step.rgb}, 0.08), inset 0 0 20px rgba(${step.rgb}, 0.04)`,
                }}
              >
                <div
                  className="text-sm font-mono tracking-widest mb-3"
                  style={{ color: `rgba(${step.rgb}, 0.9)` }}
                  aria-hidden="true"
                >
                  {step.number}
                </div>
                <h3 className="text-2xl font-light tracking-tight mb-3">
                  {step.title}
                </h3>
                <p className="text-white/60 text-sm md:text-base leading-relaxed">
                  {step.description}
                </p>
              </motion.li>
            ))}
          </ol>
        </motion.section>

        {/* Trust signals */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8 }}
          className="mb-24"
          aria-labelledby="contact-trust-heading"
        >
          <div className="text-center mb-12">
            <h2
              id="contact-trust-heading"
              className="text-4xl md:text-5xl font-light tracking-tight mb-4"
            >
              Why teams pick Cultiv
            </h2>
            <p className="text-base md:text-lg text-white/60 max-w-2xl mx-auto">
              The trade-offs AI creative should eliminate — speed, cost, quality, and direction.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {trustSignals.map((signal, idx) => {
              const { Icon } = signal;
              return (
                <motion.div
                  key={signal.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.5, delay: idx * 0.08 }}
                  className="p-6 border rounded-2xl bg-gradient-to-br from-white/[0.03] to-transparent"
                  style={{
                    borderColor: `rgba(${signal.rgb}, 0.2)`,
                    boxShadow: `0 0 16px rgba(${signal.rgb}, 0.08), inset 0 0 16px rgba(${signal.rgb}, 0.04)`,
                  }}
                >
                  <div
                    className="inline-flex items-center justify-center w-11 h-11 mb-5 rounded-xl"
                    style={{
                      background: `rgba(${signal.rgb}, 0.12)`,
                      border: `1px solid rgba(${signal.rgb}, 0.3)`,
                      color: `rgba(${signal.rgb}, 1)`,
                      boxShadow: `0 0 20px rgba(${signal.rgb}, 0.25)`,
                    }}
                  >
                    <Icon size={20} aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-medium tracking-tight mb-2">
                    {signal.title}
                  </h3>
                  <p className="text-sm text-white/60 leading-relaxed">
                    {signal.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </motion.section>

        {/* Lead magnet */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8 }}
          className="mb-4"
          aria-labelledby="contact-lead-magnet-heading"
        >
          <h2 id="contact-lead-magnet-heading" className="sr-only">
            Free AI creative teardown
          </h2>
          <LeadCaptureForm
            variant="card"
            headline="Prefer async? Get a free AI creative teardown."
          />
        </motion.section>
      </div>

      {/* FAQ */}
      <FAQ
        faqs={contactFaqs}
        title="Contact questions"
        id="contact-faq"
        schemaKey="contact-faq"
      />

      {/* Secondary exits — cross-link to Portfolio + AI Creatives so the page isn't a dead-end */}
      <div className="relative px-6 pb-24">
        <div className="max-w-3xl mx-auto text-center text-white/60 text-sm md:text-base">
          Not ready to book yet? Browse{' '}
          <Link
            to="/portfolio"
            title="See our AI viral case studies"
            className="text-white hover:text-violet-300 underline underline-offset-4 decoration-violet-400/40 hover:decoration-violet-300 transition-colors"
          >
            our AI viral case studies
          </Link>{' '}
          or head into the{' '}
          <Link
            to="/ai-creatives"
            title="Browse the AI creatives gallery"
            className="text-white hover:text-violet-300 underline underline-offset-4 decoration-violet-400/40 hover:decoration-violet-300 transition-colors"
          >
            AI creatives gallery
          </Link>{' '}
          to see what we ship.
        </div>
      </div>
    </div>
  );
};

export default Contact;
